<template>
  <VueDatePicker
    ref="datepickerRef"
    v-model="modelValue"
    class="custom-datepicker"
    :time-config="{ enableTimePicker: false }"
    :range="{ maxRange: 13 }"
    multi-calendars
    disable-year-select
    hide-offset-dates
    :config="{ closeOnClearValue: false }"
    :floating="{ arrow: false }"
    @range-start="handleRangeStart"
    @range-end="handleRangeEnd"
    @cleared="handleCleared"
  >
    <template #trigger>
      <slot />
    </template>
    <template #menu-header>
      <div
        class="flex items-center justify-center space-x-8 border-b border-[#e5e5e5] py-4"
      >
        <button @click="prevMonth">
          <SvgIcon name="arrow-left" class="size-8 text-[#a6192e]" />
        </button>
        <button @click="nextMonth">
          <SvgIcon name="arrow-right" class="size-8 text-[#a6192e]" />
        </button>
      </div>
      <button
        class="absolute right-4 top-4 flex size-8 items-center justify-center"
        @click="close"
      >
        <SvgIcon name="close" class="size-5 text-[#1F1F1F]" />
      </button>
    </template>
    <template #month-year="{ month }">
      <div
        class="flex-1 py-4 text-center text-[24px] font-semibold capitalize leading-[32px] text-[#1F1F1F]"
      >
        {{ getMonth(month) }}
      </div>
    </template>
    <template #action-row="{ modelValue: internalModelValue, selectDate }">
      <div class="flex flex-1 items-center justify-between max-md:flex-col">
        <div
          class="flex items-center text-[18px] font-semibold capitalize leading-[32px] text-[#a6192e] lg:text-[26px]"
        >
          <span
            v-if="Array.isArray(internalModelValue) && internalModelValue[0]"
            class="whitespace-nowrap"
          >
            {{ formatDate(internalModelValue[0]) }}
          </span>
          <span
            v-if="
              Array.isArray(internalModelValue) &&
              internalModelValue[0] &&
              internalModelValue[1] &&
              formatDate(internalModelValue[1]) !==
                formatDate(internalModelValue[0])
            "
            class="whitespace-nowrap"
          >
            &nbsp;-
            {{ formatDate(internalModelValue[1]) }}
          </span>
        </div>

        <div
          class="flex items-center gap-4 max-md:mt-4 max-md:w-full max-md:flex-col"
        >
          <BaseTheButtonNew text="ОТМЕНИТЬ" is-small @click="clear" />
          <BaseTheButtonNew
            type="secondary"
            text="ВЫБРАТЬ"
            is-small
            @click="selectDate"
          />
        </div>
      </div>
    </template>
    <template #day="{ day, date }">
      {{ day }}
      <template v-if="shouldShowTooltip(date)">
        <div class="day-tooltip">
          <SvgIcon
            name="click"
            class="mr-2.5 size-4 text-specialprojectsection"
          />
          <span class="text-[14px] leading-[17px] text-new-test-black">
            Для выбора одной даты кликните дважды
          </span>
        </div>
      </template>
    </template>
  </VueDatePicker>
</template>

<script setup lang="ts">
import '@vuepic/vue-datepicker/dist/main.css';

import { type ModelValue, VueDatePicker } from '@vuepic/vue-datepicker';

const modelValue = defineModel<ModelValue>('modelValue', {
  default: () => null,
});

const datepickerRef =
  useTemplateRef<InstanceType<typeof VueDatePicker>>('datepickerRef');

const monthYear = ref({
  month: new Date().getMonth(),
  year: new Date().getFullYear(),
});

const dateRange = ref<Date[]>([]);

const handleRangeStart = (date: Date) => {
  dateRange.value = [date];
};

const handleRangeEnd = (date: Date) => {
  dateRange.value = [dateRange.value[0], date];
};

const handleCleared = () => {
  dateRange.value = [];
};

const inInterval = (date: Date) => {
  const [start, end] = dateRange.value;
  if (!start || !end) return false;
  return date >= start && date <= end;
};

const shouldShowTooltip = (date: Date) => {
  if (dateRange.value.length === 1) {
    return dateRange.value[0].toISOString() === date.toISOString();
  }
  return !inInterval(date);
};

const prevMonth = () => {
  monthYear.value.month--;
  if (monthYear.value.month < 0) {
    monthYear.value.month = 11;
    monthYear.value.year--;
  }
  datepickerRef.value?.setMonthYear({
    month: monthYear.value.month,
    year: monthYear.value.year,
  });
};

const nextMonth = () => {
  monthYear.value.month++;
  if (monthYear.value.month > 11) {
    monthYear.value.month = 0;
    monthYear.value.year++;
  }
  datepickerRef.value?.setMonthYear({
    month: monthYear.value.month,
    year: monthYear.value.year,
  });
};

const getMonth = (month: number) => {
  return new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
  }).format(new Date().setMonth(month));
};

const formatDate = (date: Date | null) => {
  if (!date) return '';
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
  }).format(date);
};

const clear = () => {
  datepickerRef.value?.clearValue();
};

const close = () => {
  datepickerRef.value?.closeMenu();
};
</script>

<style lang="scss">
.custom-datepicker .dp__theme_light {
  --dp-primary-color: #f6e5e6;
  --dp-hover-color: #f6e5e6;
  --dp-range-between-dates-background-color: #f6e5e6;
  --dp-text-color: #1f1f1f;
  --dp-primary-text-color: #1f1f1f;
  --dp-cell-border-radius: 16px;
  --dp-common-transition: none;
  --dp-cell-size: 32px;
  --dp-row-margin: 0;
  --dp-action-row-padding: 16px 28px;
  --dp-multi-calendars-spacing: 0;
  --dp-border-radius: 12px;
  --dp-menu-border-color: transparent;
}

.custom-datepicker {
  .dp__menu {
    @apply shadow-[0px_0px_12px_0px_rgba(0,0,0,0.07)];
  }

  .dp__calendar_header {
    @apply hidden;
  }

  .dp__menu_inner {
    @apply p-0;

    .dp__instance_calendar {
      @apply last:border-l last:border-l-[#e5e5e5];
    }

    .dp__calendar[role='rowgroup'] {
      @apply p-4 space-y-2;
    }

    .dp__cell_inner {
      @apply relative w-[42px];
    }

    .dp__today {
      @apply bg-[#f6e5e6];
    }

    .dp__range_start.dp__range_end {
      @apply rounded-2xl;
    }
  }

  .dp__action_row {
    @apply border-t border-t-[#e5e5e5];
  }
}

.day-cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.day-tooltip {
  @apply absolute flex max-md:hidden items-center shadow-[0px_3px_4px_0px_rgba(0,0,0,0.1)] p-2 bg-white-100 left-1/2 top-full -translate-x-1/2 rounded whitespace-nowrap opacity-0 transition-[opacity,transform] pointer-events-none z-10;
}

/* Показываем тултип при наведении на ячейку */
.dp__cell_inner:hover .day-tooltip {
  @apply opacity-100 translate-y-3;
}
</style>
